// Gacha vending machine — spin the wheel for random cosmetics. Costs
// 300 crystals per pull; rolled items are granted to the inventory.

import { getCrystals, spendCrystals, grantItem, registerPull } from '../meta/wallet';
import { gachaPool, type CosmeticDef } from '../meta/cosmetics';

const PULL_COST = 300;

const POOL = gachaPool();

function rollOne(): CosmeticDef {
  const total = POOL.reduce((s, p) => s + (p.gachaWeight ?? 1), 0);
  let r = Math.random() * total;
  for (const p of POOL) {
    r -= p.gachaWeight ?? 1;
    if (r <= 0) return p;
  }
  return POOL[0];
}

export function openGacha(onClose?: () => void): () => void {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(2,6,23,.93);
    z-index: 9998;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: #e5e7eb; font: 700 14px system-ui, sans-serif;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 28px system-ui; letter-spacing: 4px; margin-bottom: 6px;
    background: linear-gradient(90deg,#f59e0b,#fbbf24,#fde047);
    -webkit-background-clip: text; background-clip: text; color: transparent;
  `;
  title.textContent = '🎰 GACHA VENDOR';
  overlay.appendChild(title);

  const sub = document.createElement('div');
  sub.style.cssText = `opacity:.65;font-size:11px;letter-spacing:2.5px;margin-bottom:14px;`;
  sub.textContent = `${PULL_COST} CRYSTALS PER PULL`;
  overlay.appendChild(sub);

  const balanceEl = document.createElement('div');
  balanceEl.style.cssText = `
    font: 800 13px system-ui; letter-spacing: 2px;
    color: #67e8f9; margin-bottom: 18px;
    padding: 5px 14px;
    background: rgba(6,182,212,.1);
    border: 1px solid #0891b2;
    border-radius: 8px;
  `;
  overlay.appendChild(balanceEl);
  function updateBalance() {
    balanceEl.textContent = `💎 ${getCrystals()} CRYSTALS`;
  }
  updateBalance();

  const resultCard = document.createElement('div');
  resultCard.style.cssText = `
    width: 260px; height: 320px;
    border-radius: 18px;
    border: 2px solid #334155;
    background: radial-gradient(circle at center, rgba(15,23,42,.95), rgba(2,6,23,.95));
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center;
    transition: transform .25s ease-out, border-color .25s;
    padding: 20px;
    box-shadow: 0 16px 44px rgba(0,0,0,.7);
  `;
  resultCard.innerHTML = `
    <div style="font:700 13px system-ui;opacity:.45;letter-spacing:2px;">PRESS PULL</div>
  `;
  overlay.appendChild(resultCard);

  const pullBtn = document.createElement('button');
  pullBtn.textContent = '✨ PULL ✨';
  pullBtn.style.cssText = `
    margin-top: 22px;
    padding: 13px 40px;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(135deg,#f59e0b,#fbbf24);
    color: #0f172a;
    cursor: pointer;
    font: 900 16px system-ui;
    letter-spacing: 2.5px;
    box-shadow: 0 12px 32px rgba(245,158,11,.45);
  `;
  overlay.appendChild(pullBtn);

  function refreshPullBtn() {
    if (getCrystals() >= PULL_COST) {
      pullBtn.textContent = '✨ PULL ✨';
      pullBtn.disabled = false;
      pullBtn.style.background = 'linear-gradient(135deg,#f59e0b,#fbbf24)';
      pullBtn.style.color = '#0f172a';
      pullBtn.style.cursor = 'pointer';
      pullBtn.style.opacity = '1';
    } else {
      pullBtn.textContent = `NEED ${PULL_COST} 💎`;
      pullBtn.disabled = true;
      pullBtn.style.background = 'rgba(15,23,42,.8)';
      pullBtn.style.color = '#64748b';
      pullBtn.style.cursor = 'not-allowed';
      pullBtn.style.opacity = '0.7';
      pullBtn.style.boxShadow = 'none';
    }
  }
  refreshPullBtn();

  pullBtn.addEventListener('click', () => {
    if (!spendCrystals(PULL_COST)) return;
    registerPull();
    const pick = rollOne();
    grantItem(pick.id);
    updateBalance();
    refreshPullBtn();
    resultCard.style.borderColor = pick.color;
    resultCard.style.transform = 'scale(1.07)';
    resultCard.innerHTML = `
      <div style="font-size:80px;margin-bottom:10px;filter: drop-shadow(0 0 24px ${pick.color});">${pick.icon}</div>
      <div style="font:900 19px system-ui;color:${pick.color};text-shadow: 0 0 18px ${pick.color};">${pick.name}</div>
      <div style="font:800 11px system-ui;letter-spacing:3px;opacity:.75;margin-top:8px;text-transform:uppercase;">${pick.rarity ?? ''} · ${pick.slot}</div>
      <div style="font:700 10px system-ui;opacity:.55;margin-top:6px;letter-spacing:1.5px;">Equip from the Cosmetic Shop</div>
    `;
    setTimeout(() => { resultCard.style.transform = 'scale(1)'; }, 250);
  });

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'CLOSE [ESC]';
  closeBtn.style.cssText = `
    margin-top: 16px;
    padding: 8px 22px;
    border-radius: 8px;
    border: 1px solid #475569;
    background: rgba(15,23,42,.85);
    color: #e5e7eb;
    cursor: pointer;
    font: 700 12px system-ui;
    letter-spacing: 1.5px;
  `;
  overlay.appendChild(closeBtn);

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', onKey);
    overlay.remove();
    onClose?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') close();
  };
  addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', close);

  document.body.appendChild(overlay);
  return close;
}
